import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";

/* ─────── Phase 7c — render image attachments in a message bubble ───────
 * `attachments` is the public projection from the backend:
 *   [{ id, filename, content_type, size_bytes, width, height, preview_url }]
 * Up to 4 thumbs render inline; rest collapse behind a "+N" tile that opens
 * the lightbox.  All thumbnails are clickable. The lightbox supports keyboard
 * ← → to navigate, Esc to close, and click-outside to close.
 */

const MAX_INLINE_THUMBS = 4;

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Lightbox({ items, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex || 0);
  const item = items[idx];

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight")
        setIdx((i) => (i + 1) % items.length);
      else if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, items.length]);

  if (!item || typeof document === "undefined") return null;

  const node = (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0E0D0C]/85"
      data-testid="attachment-lightbox"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="attachment-lightbox-close"
        className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <X size={18} strokeWidth={2.2} />
      </button>
      {/* Prev / Next */}
      {items.length > 1 ? (
        <>
          <button
            type="button"
            onClick={() =>
              setIdx((i) => (i - 1 + items.length) % items.length)
            }
            data-testid="attachment-lightbox-prev"
            className="absolute left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % items.length)}
            data-testid="attachment-lightbox-next"
            className="absolute right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronRight size={20} strokeWidth={2} />
          </button>
        </>
      ) : null}
      {/* Image */}
      <img
        src={item.preview_url}
        alt={item.filename || "attachment"}
        className="max-w-[90vw] max-h-[80vh] object-contain rounded-md shadow-2xl"
        data-testid="attachment-lightbox-image"
      />
      {/* Footer */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-4 px-4 py-2 rounded-full bg-white/10 text-white text-xs font-sans backdrop-blur-md">
        <span data-testid="attachment-lightbox-filename" className="font-medium">
          {item.filename || "image"}
        </span>
        {item.size_bytes ? (
          <span className="opacity-70">{fmtBytes(item.size_bytes)}</span>
        ) : null}
        <a
          href={item.preview_url}
          download={item.filename || "image"}
          data-testid="attachment-lightbox-download"
          className="flex items-center gap-1 hover:text-[#F1E0CE]"
        >
          <Download size={13} strokeWidth={2} /> Download
        </a>
        {items.length > 1 ? (
          <span className="opacity-70">
            {idx + 1} / {items.length}
          </span>
        ) : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

export default function MessageAttachments({
  attachments,
  messageId,
  alignment, // "left" | "right"
}) {
  const list = useMemo(
    () => (Array.isArray(attachments) ? attachments : []),
    [attachments]
  );
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);

  if (list.length === 0) return null;

  const visible = list.slice(0, MAX_INLINE_THUMBS);
  const overflow = list.length - visible.length;
  const justify = alignment === "right" ? "justify-end" : "justify-start";

  return (
    <div
      className={`mt-2 mb-1 flex flex-wrap gap-2 ${justify}`}
      data-testid={`message-attachments-${messageId}`}
      data-count={list.length}
    >
      {visible.map((a, i) => (
        <button
          type="button"
          key={a.id || a.local_id || i}
          onClick={() => setLightboxIdx(i)}
          data-testid={`attachment-thumb-${messageId}-${i}`}
          className="group relative block rounded-lg overflow-hidden border border-[#DCD7CE] hover:border-[#D96C4A]/60 bg-[#FBF8F2] transition-colors"
          aria-label={`Open ${a.filename || "image"}`}
        >
          <img
            src={a.preview_url}
            alt={a.filename || "attachment"}
            className="w-32 h-32 object-cover block"
            loading="lazy"
          />
          {a.pending ? (
            <span
              className="absolute inset-x-0 bottom-0 h-1 bg-[#D96C4A]/30"
              data-testid={`attachment-thumb-pending-${messageId}-${i}`}
            >
              <span
                className="block h-full bg-[#D96C4A] transition-all"
                style={{ width: `${Math.max(0, Math.min(100, a.progress || 0))}%` }}
              />
            </span>
          ) : null}
        </button>
      ))}
      {overflow > 0 ? (
        <button
          type="button"
          onClick={() => setLightboxIdx(MAX_INLINE_THUMBS)}
          data-testid={`attachment-overflow-${messageId}`}
          className="w-32 h-32 rounded-lg border border-[#DCD7CE] bg-[#F3F1EC] hover:bg-[#EFEAE0] flex items-center justify-center font-sans text-sm font-medium text-[#2C2A29]"
        >
          + {overflow} more
        </button>
      ) : null}
      {lightboxIdx !== null ? (
        <Lightbox
          items={list}
          startIndex={Math.min(lightboxIdx, list.length - 1)}
          onClose={closeLightbox}
        />
      ) : null}
    </div>
  );
}
